import { carouselHeroMessages } from './messages/carousel-hero.messages';

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';
import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluencePageAnchorLpConfEngineeringRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorEngineering = (
	pageProps: ConfluencePageAnchorLpConfEngineeringRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	const pageId = 'anchor-lp-conf-team-engineering';

	let customHeroSection = null;
	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.dynamic-documentation.title',
				defaultMessage: 'Dynamic documentation',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.dynamic-documentation.body',
				defaultMessage:
					'Brainstorm workflows, plan sprints, and take release notes—all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/0du4h6u1u85gdfw51btwg1w486m05ehy.webp',
			imgAltText: 'A Confluence page with advanced roadmap integration',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.get-everyone-on-the-same-page.title',
				defaultMessage: 'Get everyone on the same page',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.get-everyone-on-the-same-page.body',
				defaultMessage:
					'Collaborate with cross-functional teams using real-time editing and inline comments.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/b2d5d0n6c3585bx8t07l0kmlsd36648q.webp',
			imgAltText: 'A Confluence page with multiple teammates editing and leaving comments',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.the-context-software-developers-need.title',
				defaultMessage: 'The context software developers need',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Engineering.the-context-software-developers-need.body',
				defaultMessage:
					'Stay organized with dedicated team workspaces, all equipped with AI-powered search.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/21k4hektb512gekkj3ibm325ahan4vrh.webp',
			imgAltText: 'A Confluence page titled Project Hub',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': intl.formatMessage(carouselHeroMessages.ariaLabel),
		id: 'confluence-engineering-carousel',
		slides: carouselSlides,
	};

	return (
		<ConfluencePagePaidLpConflAnchor
			{...pageProps}
			pageId={pageId}
			persona={'unknown' as PaidLandingPersonaTypes}
			pageVariant="engineering"
			dataProductKey="confluence-PaidLpConflEngineering"
			carouselHeroProps={carouselHeroProps}
			keyword={pageProps.keyword}
			customHeroSection={customHeroSection}
			teamType=""
		/>
	);
};
